import React, { useEffect, useRef, useState } from 'react';
import { X, Bot, User, Upload, Loader, Check, RefreshCw, Pencil, SkipForward, Sparkles, FileEdit } from 'lucide-react';
import clsx from 'clsx';
import apiClient from '../services/api/client';
import { ProductService } from '../services/productService';
import { eanValido } from './AddProductModal';

// ─────────────────────────────────────────────────────────────────────────────
// Cadastro de produto via chat: o assistente pergunta um passo de cada vez e o
// lojista aprova. A IA analisa a foto, sugere título, descrição, categorias e
// embalagem; a ficha técnica do Mercado Livre é perguntada campo a campo.
// No fim cria o produto (mesmo formato do cadastro manual) ou abre o formulário.
// ─────────────────────────────────────────────────────────────────────────────

const AI_REQ = { timeout: 90000 };

type Msg = { id: number; from: 'bot' | 'user'; text: string; img?: string };
type Attr = { name: string; value: string; required: boolean; mlId?: string };
type CatOpc = { id: string; caminho: string };
type MlAttr = { id: string; name: string; obrigatorio: boolean; filtro: boolean; valores: { id: string; name: string }[]; dica?: string };
type Passo =
  | 'foto' | 'confirmaProduto' | 'corrigeProduto' | 'titulo' | 'editaTitulo' | 'marca' | 'descricao' | 'editaDescricao'
  | 'marketplaces' | 'categoriaQflow' | 'categoriaMp' | 'ean' | 'sku' | 'preco' | 'estoque' | 'embalagem'
  | 'editaEmbalagem' | 'fichaResumo' | 'fichaCampo' | 'midias' | 'revisao' | 'criando' | 'fim';

export type RascunhoProduto = Record<string, any>;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Abre o formulário completo já preenchido com o que foi respondido no chat */
  onAbrirFormulario: (rascunho: RascunhoProduto, marketplaces: string[]) => void;
}

// Mesmos padrões do cadastro manual (AddProductModal)
const PADROES = {
  ml_condition: 'new', ml_listing_type: 'free', ml_buying_mode: 'buy_it_now', ml_currency: 'BRL',
  magalu_origin: 'NACIONAL', magalu_warranty_time: '90', magalu_warranty_text: 'Garantia do fabricante',
  amazon_condition: 'New', shopee_condition: 'NEW', shopee_pre_order: 'false', shopee_days_to_ship: '0',
  carrefour_logistic_class: 'STANDARD', leroy_country_of_origin: 'BR',
};

const ESPECIAIS_ML = new Set(['BRAND', 'GTIN', 'SELLER_SKU', 'SIZE_GRID_ID', 'SIZE_GRID_ROW_ID', 'EMPTY_GTIN_REASON',
  'ITEM_CONDITION', 'PRODUCT_DATA_SOURCE']);

const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const num = (v: string) => parseFloat(String(v || '').replace(',', '.')) || 0;

export default function ChatProductWizard({ isOpen, onClose, onSuccess, onAbrirFormulario }: Props) {
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [passo, setPasso] = useState<Passo>('foto');
  const [busy, setBusy] = useState('');
  const [texto, setTexto] = useState('');
  const [erroInput, setErroInput] = useState('');
  const fimRef = useRef<HTMLDivElement | null>(null);
  const idRef = useRef(0);

  // Contexto do lojista
  const [mpsConectados, setMpsConectados] = useState<{ id: string; name: string }[]>([]);
  const [catsQflow, setCatsQflow] = useState<string[]>([]);

  // Rascunho do produto
  const [fotoArquivo, setFotoArquivo] = useState<File | null>(null);
  const [imagens, setImagens] = useState<string[]>([]);
  const [analise, setAnalise] = useState<any>(null);
  const [titulo, setTitulo] = useState('');
  const [marca, setMarca] = useState('');
  const [descricao, setDescricao] = useState('');
  const [mps, setMps] = useState<string[]>([]);
  const [categoria, setCategoria] = useState('');
  const [catIds, setCatIds] = useState<Record<string, string>>({});
  const [catOpcoes, setCatOpcoes] = useState<Record<string, CatOpc[]>>({});
  const [filaCat, setFilaCat] = useState<string[]>([]);
  const [ean, setEan] = useState('');
  const [sku, setSku] = useState('');
  const [preco, setPreco] = useState('');
  const [estoque, setEstoque] = useState('');
  const [emb, setEmb] = useState({ peso_kg: '', altura_cm: '', largura_cm: '', comprimento_cm: '' });
  const [attrs, setAttrs] = useState<Attr[]>([]);
  const [filaFicha, setFilaFicha] = useState<MlAttr[]>([]);
  const [sugestao, setSugestao] = useState('');      // valor proposto pela IA no passo atual

  const bot = (text: string, img?: string) => setMsgs(m => [...m, { id: ++idRef.current, from: 'bot', text, img }]);
  const eu = (text: string, img?: string) => setMsgs(m => [...m, { id: ++idRef.current, from: 'user', text, img }]);
  const erroIA = (e: any, fallback: string) => e?.response?.data?.error || e?.message || fallback;
  const nomeMp = (id: string) => mpsConectados.find(m => m.id === id)?.name || id;

  // ── início ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    idRef.current = 0;
    setMsgs([]); setPasso('foto'); setBusy(''); setTexto(''); setErroInput('');
    setFotoArquivo(null); setImagens([]); setAnalise(null); setTitulo(''); setMarca(''); setDescricao('');
    setMps([]); setCategoria(''); setCatIds({}); setCatOpcoes({}); setFilaCat([]); setEan(''); setSku('');
    setPreco(''); setEstoque(''); setEmb({ peso_kg: '', altura_cm: '', largura_cm: '', comprimento_cm: '' });
    setAttrs([]); setFilaFicha([]); setSugestao('');
    bot('Olá! Vou te ajudar a cadastrar o produto. 📸 Comece enviando a **foto principal** do produto.');
    apiClient.get('/ai/cadastro/contexto').then(r => {
      setMpsConectados(r.data.marketplaces || []);
      setCatsQflow(r.data.categorias || []);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => { fimRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs, passo, busy]);

  // ── 1. foto ──────────────────────────────────────────────────────────────
  const uploadArquivo = async (file: File): Promise<string> => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('userCode', localStorage.getItem('userCode') || '');
    const r = await apiClient.post('/products/upload-media', fd, { timeout: 120000 });
    return r.data.url;
  };

  const analisar = async (file: File, dica = '') => {
    setBusy('Analisando a foto…');
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (dica) fd.append('dica', dica);
      const r = await apiClient.post('/ai/cadastro/analisar-imagem', fd, AI_REQ);
      const a = r.data || {};
      setAnalise(a);
      const novos: Attr[] = [];
      const add = (name: string, value: any) => {
        const v = String(value || '').trim();
        if (!v || /^(n[aã]o se aplica|outro)$/i.test(v)) return;
        if (novos.some(n => n.name.toLowerCase() === name.toLowerCase())) return;
        novos.push({ name, value: v, required: false });
      };
      add('Cor', a.cor); add('Material', a.material); add('Estampa', a.estampa); add('Gênero', a.genero);
      (a.atributos || []).forEach((x: any) => x?.name && add(String(x.name), x.value));
      setAttrs(novos);
      if (a.marca_visivel) setMarca(a.marca_visivel);
      bot(`Identifiquei: **${a.resumo || a.produto}**${a.confianca === 'baixa' ? ' (não tenho muita certeza)' : ''}. Está certo?`);
      setPasso('confirmaProduto');
    } catch (e: any) {
      bot(`Não consegui analisar a foto: ${erroIA(e, 'erro desconhecido')}. Me conte em poucas palavras o que é o produto.`);
      setPasso('corrigeProduto');
    } finally { setBusy(''); }
  };

  const onFoto = async (file: File) => {
    if (!file.type.startsWith('image/')) { setErroInput('Envie uma imagem (JPG, PNG ou WEBP).'); return; }
    setErroInput('');
    eu('Foto principal', URL.createObjectURL(file));
    setFotoArquivo(file);
    setBusy('Enviando a foto…');
    try {
      const url = await uploadArquivo(file);
      setImagens([url]);
    } catch (e: any) {
      bot(`Não consegui salvar a foto: ${erroIA(e, 'erro no upload')}. Tente outra foto.`);
      setBusy(''); return;
    }
    await analisar(file);
  };

  // ── 2. título e marca ────────────────────────────────────────────────────
  const gerarTitulo = async (resumo?: string) => {
    setBusy('Criando o título…');
    try {
      const r = await apiClient.post('/ai/generate-title', {
        brand: marca, category: categoria, attributes: attrs,
        currentDescription: resumo || analise?.resumo || analise?.produto || '',
      }, AI_REQ);
      const t = r.data.title || '';
      setSugestao(t);
      bot(`Sugestão de título (${t.length}/60):\n**${t}**`);
      setPasso('titulo');
    } catch (e: any) {
      bot(`Não consegui gerar o título (${erroIA(e, 'IA indisponível')}). Escreva o título do produto.`);
      setPasso('editaTitulo');
    } finally { setBusy(''); }
  };

  const confirmarTitulo = (t: string) => {
    setTitulo(t);
    bot(marca ? `Vi a marca **${marca}** no produto. É essa? (pode corrigir abaixo)` : 'Qual é a **marca** do produto?');
    setTexto(marca);
    setPasso('marca');
  };

  // ── 3. descrição ─────────────────────────────────────────────────────────
  const gerarDescricao = async (m = marca, t = titulo) => {
    setBusy('Escrevendo a descrição…');
    try {
      const r = await apiClient.post('/ai/generate-description', {
        title: t, brand: m, category: categoria, attributes: attrs,
        currentDescription: analise?.resumo || '',
      }, AI_REQ);
      setSugestao(r.data.description || '');
      bot(`Sugestão de descrição:\n\n${r.data.description}`);
      setPasso('descricao');
    } catch (e: any) {
      bot(`Não consegui gerar a descrição (${erroIA(e, 'IA indisponível')}). Escreva a descrição.`);
      setPasso('editaDescricao');
    } finally { setBusy(''); }
  };

  // ── 4. marketplaces e categorias ────────────────────────────────────────
  const perguntarMarketplaces = (desc: string) => {
    if (!mpsConectados.length) {
      bot('Você ainda não tem marketplaces conectados no QFlow (Configurações). Vou seguir e o produto fica salvo só no catálogo.');
      perguntarCategoriaQflow([], desc);
      return;
    }
    setMps(mpsConectados.map(m => m.id));
    bot('Em quais **marketplaces** você quer anunciar? Marquei todos que estão conectados na sua conta.');
    setPasso('marketplaces');
  };

  const perguntarCategoriaQflow = async (escolhidos: string[], desc = descricao) => {
    if (!catsQflow.length) { buscarCategoriasMp(escolhidos); return; }
    setBusy('Escolhendo a categoria do catálogo…');
    let c = '';
    try {
      const r = await apiClient.post('/ai/suggest-category', { title: titulo, brand: marca, description: desc, categories: catsQflow }, AI_REQ);
      c = catsQflow.find(x => x.toLowerCase() === String(r.data.category || '').toLowerCase()) || '';
    } catch { /* segue sem sugestão */ }
    setSugestao(c);
    bot(c ? `No seu catálogo, a categoria seria **${c}**. Confirma?` : 'Em qual **categoria do seu catálogo** ele entra?');
    setBusy('');
    setPasso('categoriaQflow');
  };

  const buscarCategoriasMp = async (escolhidos: string[]) => {
    if (!escolhidos.length) { perguntarEan(); return; }
    setBusy('Buscando as categorias nos marketplaces…');
    try {
      const r = await apiClient.post('/ai/cadastro/categorias', {
        titulo, marketplaces: escolhidos, termos: analise?.palavras_busca || [],
      }, AI_REQ);
      const ops: Record<string, CatOpc[]> = r.data.categorias || {};
      setCatOpcoes(ops);
      mostrarCategoriaMp(escolhidos, ops);
    } catch (e: any) {
      bot(`Não consegui buscar as categorias (${erroIA(e, 'erro')}). Você pode escolher depois no formulário.`);
      perguntarEan();
    } finally { setBusy(''); }
  };

  /** Mostra a categoria do 1º marketplace da fila; os sem sugestão são pulados. */
  const mostrarCategoriaMp = (fila: string[], ops: Record<string, CatOpc[]>) => {
    let resto = [...fila];
    while (resto.length && !(ops[resto[0]] || []).length) {
      bot(`Não encontrei categoria no **${nomeMp(resto[0])}** para esse produto — você escolhe depois no formulário.`);
      resto = resto.slice(1);
    }
    setFilaCat(resto);
    if (!resto.length) { perguntarEan(); return; }
    bot(`Categoria no **${nomeMp(resto[0])}** — escolha a melhor (a primeira é a minha sugestão):`);
    setPasso('categoriaMp');
  };

  const escolherCategoria = (mp: string, op: CatOpc | null) => {
    if (op) { setCatIds(c => ({ ...c, [mp]: op.id })); eu(`${nomeMp(mp)}: ${op.caminho}`); }
    else eu(`${nomeMp(mp)}: escolho depois`);
    mostrarCategoriaMp(filaCat.filter(x => x !== mp), catOpcoes);
  };

  // ── 5. EAN, SKU, preço, estoque, embalagem ──────────────────────────────
  const perguntarEan = () => { bot('Qual é o **EAN** (código de barras) do produto?'); setTexto(''); setPasso('ean'); };

  const perguntarSku = () => {
    bot('Você tem um **código SKU** para ele? Se não tiver, eu crio o próximo número da sua sequência.');
    setTexto(''); setPasso('sku');
  };

  const perguntarPreco = () => { bot('Qual é o **preço** de venda (R$)?'); setTexto(''); setPasso('preco'); };

  const gerarSku = async () => {
    setBusy('Gerando o SKU…');
    try {
      const r = await apiClient.get('/products/next-sku');
      setSku(r.data.sku); eu(`SKU automático: ${r.data.sku}`);
      perguntarPreco();
    } catch (e: any) {
      bot(`Não consegui gerar o SKU (${erroIA(e, 'erro')}). Digite um SKU.`);
    } finally { setBusy(''); }
  };

  const estimarEmbalagem = async () => {
    setBusy('Calculando peso e medidas da embalagem…');
    try {
      const r = await apiClient.post('/ai/cadastro/embalagem', { titulo, descricao }, AI_REQ);
      const e = { peso_kg: String(r.data.peso_kg || ''), altura_cm: String(r.data.altura_cm || ''),
                  largura_cm: String(r.data.largura_cm || ''), comprimento_cm: String(r.data.comprimento_cm || '') };
      setEmb(e);
      bot(`Para o frete, estimei a **embalagem** em: ${e.peso_kg} kg · ${e.altura_cm} × ${e.largura_cm} × ${e.comprimento_cm} cm ` +
          `(altura × largura × comprimento). Confere com o pacote real?`);
      setPasso('embalagem');
    } catch {
      bot('Qual o **peso (kg)** e as **medidas (cm)** da embalagem de envio?');
      setPasso('editaEmbalagem');
    } finally { setBusy(''); }
  };

  // ── 6. ficha técnica do Mercado Livre, campo a campo ────────────────────
  const perguntarMidias = () => {
    bot('Tem **mais fotos ou vídeos** do produto? Pode enviar vários de uma vez.');
    setPasso('midias');
  };

  const perguntarCampo = (fila: MlAttr[]) => {
    const a = fila[0];
    if (!a) { bot('Ficha técnica do Mercado Livre concluída. ✅'); perguntarMidias(); return; }
    bot(`**${a.name}**${a.obrigatorio ? ' (obrigatório)' : ' (ajuda nos filtros de busca)'}${a.dica ? `\n_${a.dica}_` : ''}`);
    setTexto(''); setPasso('fichaCampo');
  };

  const iniciarFicha = async () => {
    if (!mps.includes('ml') || !catIds.ml) { perguntarMidias(); return; }
    setBusy('Carregando a ficha técnica do Mercado Livre…');
    try {
      const r = await apiClient.post(`/ml/categories/${catIds.ml}/ficha`, {
        title: titulo, description: descricao, brand: marca, attributes: attrs, category: categoria,
      }, AI_REQ);
      const ficha = r.data.ficha || {};
      const pre = r.data.preenchimento || {};
      const lista: MlAttr[] = (ficha.atributos || []).filter((a: MlAttr) => !ESPECIAIS_ML.has(a.id));
      // o que a ficha já conseguiu preencher com os dados do produto
      const auto: Attr[] = [];
      (pre.atributos || []).forEach((x: any) => {
        const a = lista.find(l => l.id === x.id);
        if (a && x.value_name) auto.push({ name: a.name, value: x.value_name, required: a.obrigatorio, mlId: a.id });
      });
      const faltam = lista.filter(a => !auto.some(x => x.mlId === a.id))
        .sort((a, b) => Number(b.obrigatorio) - Number(a.obrigatorio));
      setAttrs(prev => [...prev.filter(p => !auto.some(x => x.mlId === p.mlId || x.name.toLowerCase() === p.name.toLowerCase())), ...auto]);
      setFilaFicha(faltam);
      if (auto.length) {
        bot(`Ficha técnica do Mercado Livre (${ficha.caminho}). Já preenchi com os dados do produto:\n` +
            auto.map(a => `• ${a.name}: **${a.value}**`).join('\n') +
            (faltam.length ? `\n\nFaltam ${faltam.length} campo(s). Vamos um por um?` : '\n\nFicha completa!'));
        setPasso('fichaResumo');
      } else if (faltam.length) {
        bot(`Ficha técnica do Mercado Livre (${ficha.caminho}): ${faltam.length} campo(s) para preencher. Vamos um por um.`);
        perguntarCampo(faltam);
      } else perguntarMidias();
    } catch (e: any) {
      bot(`Não consegui carregar a ficha do Mercado Livre (${erroIA(e, 'erro')}). Você completa depois no formulário.`);
      perguntarMidias();
    } finally { setBusy(''); }
  };

  const responderCampo = (valor: string | null) => {
    const a = filaFicha[0];
    if (!a) return;
    if (valor) {
      eu(valor);
      setAttrs(prev => [...prev.filter(p => p.mlId !== a.id && p.name.toLowerCase() !== a.name.toLowerCase()),
        { name: a.name, value: valor, required: a.obrigatorio, mlId: a.id }]);
    } else eu('Pular');
    const resto = filaFicha.slice(1);
    setFilaFicha(resto);
    perguntarCampo(resto);
  };

  // ── 7. mais fotos/vídeos ─────────────────────────────────────────────────
  const onMidias = async (files: FileList) => {
    const arr = Array.from(files).filter(f => f.type.startsWith('image/') || f.type.startsWith('video/'));
    if (!arr.length) return;
    eu(`${arr.length} arquivo(s)`);
    setBusy(`Enviando ${arr.length} arquivo(s)…`);
    const ok: string[] = [];
    for (const f of arr) {
      try { ok.push(await uploadArquivo(f)); } catch { /* segue os outros */ }
    }
    setImagens(prev => [...prev, ...ok]);
    setBusy('');
    bot(`${ok.length} de ${arr.length} arquivo(s) enviado(s). Quer enviar mais?`);
  };

  const irParaRevisao = () => { bot('Pronto! Confira o resumo do produto:'); setPasso('revisao'); };

  // ── 8. criar ─────────────────────────────────────────────────────────────
  const montarProduto = (): RascunhoProduto => {
    const e = ean.replace(/\D/g, '');
    const cats: Record<string, string> = {};
    Object.entries(catIds).forEach(([mp, id]) => { cats[`${mp}_category_id`] = id; });
    return {
      ...PADROES, ...cats,
      sku: sku.trim(), title: titulo, description: descricao, category: categoria, brand: marca || 'Sem marca',
      basePrice: preco, stock: estoque,
      weight: emb.peso_kg, height: emb.altura_cm, width: emb.largura_cm, length: emb.comprimento_cm,
      ean: e, magalu_ean: e, casas_ean: e, carrefour_ean: e, leroy_ean: e,
      attributes: attrs,
      images: imagens.map(url => ({ url, allowedMarketplaces: [...mps] })),
    };
  };

  const criar = async () => {
    setPasso('criando'); setBusy('Criando o produto…');
    const r = montarProduto();
    const imgs = r.images as { url: string; allowedMarketplaces: string[] }[];
    try {
      await ProductService.createProduct({
        ...r,
        basePrice: num(preco), stock: parseInt(estoque) || 0,
        weight: num(emb.peso_kg), height: num(emb.altura_cm), width: num(emb.largura_cm), length: num(emb.comprimento_cm),
        marketplaces: mps,
        image: imgs[0]?.url || '',
        images: imgs.map(i => i.url),
        imagesConfig: imgs,
      } as any);
      bot(`✅ Produto **${sku}** criado${mps.length ? ` e enviado para ${mps.map(nomeMp).join(', ')}` : ''}. Acompanhe o status no Catálogo.`);
      setPasso('fim');
      onSuccess();
    } catch (e: any) {
      bot(`Não consegui criar o produto: ${e.message || 'erro'}. Corrija abaixo ou use o formulário completo.`);
      setPasso('revisao');
    } finally { setBusy(''); }
  };

  // ── envio de texto (passos que esperam digitação) ───────────────────────
  const enviarTexto = () => {
    const v = texto.trim();
    setErroInput('');
    switch (passo) {
      case 'corrigeProduto':
        if (!v) return;
        eu(v); setTexto('');
        if (fotoArquivo) analisar(fotoArquivo, v);
        else { setAnalise({ resumo: v, produto: v }); gerarTitulo(v); }
        return;
      case 'editaTitulo':
        if (!v) return;
        if (v.length > 60) { setErroInput(`O título tem ${v.length} caracteres (máximo 60).`); return; }
        eu(v); setTexto(''); confirmarTitulo(v); return;
      case 'marca': {
        const m = v || 'Sem marca';
        eu(m); setMarca(m); setTexto(''); gerarDescricao(m); return;
      }
      case 'editaDescricao':
        if (!v) return;
        eu('Descrição escrita por mim'); setDescricao(v); setTexto(''); perguntarMarketplaces(v); return;
      case 'categoriaQflow':
        if (!v) return;
        eu(v); setCategoria(v); setTexto(''); buscarCategoriasMp(mps); return;
      case 'ean': {
        const d = v.replace(/\D/g, '');
        if (!eanValido(d)) { setErroInput('EAN inválido — confira os números (8, 12, 13 ou 14 dígitos).'); return; }
        eu(d); setEan(d); setTexto(''); perguntarSku(); return;
      }
      case 'sku':
        if (!v) return;
        if (/\s/.test(v)) { setErroInput('O SKU não pode ter espaços.'); return; }
        eu(v); setSku(v); setTexto(''); perguntarPreco(); return;
      case 'preco': {
        const n = parseFloat(v.replace(/[^\d,.-]/g, '').replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.'));
        if (!n || n <= 0) { setErroInput('Informe um preço válido, ex.: 59,90'); return; }
        eu(brl(n)); setPreco(String(n)); setTexto(''); bot('Quantas **unidades em estoque**?'); setPasso('estoque'); return;
      }
      case 'estoque': {
        const n = parseInt(v.replace(/\D/g, ''), 10);
        if (isNaN(n) || n < 0) { setErroInput('Informe a quantidade, ex.: 10'); return; }
        eu(String(n)); setEstoque(String(n)); setTexto(''); estimarEmbalagem(); return;
      }
      case 'fichaCampo':
        if (!v) return;
        setTexto(''); responderCampo(v); return;
    }
  };

  if (!isOpen) return null;

  // ── renderização ─────────────────────────────────────────────────────────
  const btn = 'px-3 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 transition-colors disabled:opacity-40';
  const prim = `${btn} bg-primary-600 hover:bg-primary-500 text-white`;
  const sec = `${btn} border border-dark-600 text-dark-200 hover:bg-dark-800`;
  const esperaTexto = ['corrigeProduto', 'editaTitulo', 'marca', 'editaDescricao', 'categoriaQflow', 'ean', 'sku',
    'preco', 'estoque', 'fichaCampo'].includes(passo);
  const campoAtual = filaFicha[0];
  const mpAtual = filaCat[0];
  const fmt = (t: string) => t.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith('**') && p.endsWith('**') ? <strong key={i}>{p.slice(2, -2)}</strong> : <React.Fragment key={i}>{p}</React.Fragment>);

  const acoes = (() => {
    if (busy) return null;
    switch (passo) {
      case 'foto':
        return (
          <label className={clsx(prim, 'cursor-pointer')}>
            <Upload className="w-4 h-4" /> Enviar foto
            <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && onFoto(e.target.files[0])} />
          </label>
        );
      case 'confirmaProduto':
        return (<>
          <button className={prim} onClick={() => { eu('Sim, está certo'); gerarTitulo(); }}><Check className="w-4 h-4" /> Sim, está certo</button>
          <button className={sec} onClick={() => { eu('Não, vou corrigir'); bot('Me conte o que é o produto (ex.: macacão de bebê em plush, azul, tamanho G).'); setPasso('corrigeProduto'); }}>
            <Pencil className="w-4 h-4" /> Corrigir
          </button>
        </>);
      case 'titulo':
        return (<>
          <button className={prim} onClick={() => { eu('Aprovado'); confirmarTitulo(sugestao); }}><Check className="w-4 h-4" /> Aprovar</button>
          <button className={sec} onClick={() => { eu('Gerar outro'); gerarTitulo(); }}><RefreshCw className="w-4 h-4" /> Gerar outro</button>
          <button className={sec} onClick={() => { setTexto(sugestao); setPasso('editaTitulo'); }}><Pencil className="w-4 h-4" /> Editar</button>
        </>);
      case 'marca':
        return <button className={sec} onClick={() => { eu('Sem marca'); setMarca('Sem marca'); setTexto(''); gerarDescricao('Sem marca'); }}>Sem marca</button>;
      case 'descricao':
        return (<>
          <button className={prim} onClick={() => { eu('Aprovada'); setDescricao(sugestao); perguntarMarketplaces(sugestao); }}><Check className="w-4 h-4" /> Aprovar</button>
          <button className={sec} onClick={() => { eu('Gerar outra'); gerarDescricao(); }}><RefreshCw className="w-4 h-4" /> Gerar outra</button>
          <button className={sec} onClick={() => { setTexto(sugestao); setPasso('editaDescricao'); }}><Pencil className="w-4 h-4" /> Editar</button>
        </>);
      case 'marketplaces':
        return (
          <div className="w-full space-y-2">
            <div className="flex flex-wrap gap-2">
              {mpsConectados.map(m => (
                <label key={m.id} className={clsx('px-3 py-1.5 rounded-lg border text-sm cursor-pointer',
                  mps.includes(m.id) ? 'border-primary-500 bg-primary-500/10 text-dark-100' : 'border-dark-600 text-dark-400')}>
                  <input type="checkbox" className="mr-1.5" checked={mps.includes(m.id)}
                         onChange={e => setMps(p => e.target.checked ? [...p, m.id] : p.filter(x => x !== m.id))} />
                  {m.name}
                </label>
              ))}
            </div>
            <button className={prim} onClick={() => { eu(mps.length ? mps.map(nomeMp).join(', ') : 'Nenhum por enquanto'); perguntarCategoriaQflow(mps); }}>
              <Check className="w-4 h-4" /> Confirmar
            </button>
          </div>
        );
      case 'categoriaQflow':
        return (<>
          {sugestao && <button className={prim} onClick={() => { eu(sugestao); setCategoria(sugestao); buscarCategoriasMp(mps); }}><Check className="w-4 h-4" /> {sugestao}</button>}
          {catsQflow.length > 0 && (
            <select className="px-3 py-2 rounded-lg border border-dark-600 bg-dark-900 text-dark-100 text-sm" value=""
                    onChange={e => { if (e.target.value) { eu(e.target.value); setCategoria(e.target.value); buscarCategoriasMp(mps); } }}>
              <option value="">Outra categoria…</option>
              {catsQflow.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </>);
      case 'categoriaMp':
        return mpAtual ? (
          <div className="w-full space-y-1.5">
            {(catOpcoes[mpAtual] || []).map((o, i) => (
              <button key={o.id} onClick={() => escolherCategoria(mpAtual, o)}
                      className={clsx('w-full text-left px-3 py-2 rounded-lg border text-sm',
                        i === 0 ? 'border-primary-500 bg-primary-500/10 text-dark-100' : 'border-dark-600 text-dark-200 hover:bg-dark-800')}>
                {i === 0 && <Sparkles className="w-3.5 h-3.5 inline mr-1 text-primary-500" />}{o.caminho} <span className="text-dark-500">({o.id})</span>
              </button>
            ))}
            <button className={sec} onClick={() => escolherCategoria(mpAtual, null)}><SkipForward className="w-4 h-4" /> Escolho depois</button>
          </div>
        ) : null;
      case 'ean':
        return <button className={sec} onClick={() => { eu('Não tenho EAN'); setEan(''); perguntarSku(); }}>Não tenho EAN</button>;
      case 'sku':
        return <button className={prim} onClick={gerarSku}><Sparkles className="w-4 h-4" /> Gerar automático</button>;
      case 'embalagem':
        return (<>
          <button className={prim} onClick={() => { eu('Confere'); iniciarFicha(); }}><Check className="w-4 h-4" /> Confere</button>
          <button className={sec} onClick={() => setPasso('editaEmbalagem')}><Pencil className="w-4 h-4" /> Ajustar</button>
        </>);
      case 'editaEmbalagem':
        return (
          <div className="w-full space-y-2">
            <div className="grid grid-cols-4 gap-2">
              {([['peso_kg', 'Peso (kg)'], ['altura_cm', 'Altura (cm)'], ['largura_cm', 'Largura (cm)'], ['comprimento_cm', 'Compr. (cm)']] as const).map(([k, l]) => (
                <label key={k} className="text-xs text-dark-400">{l}
                  <input value={(emb as any)[k]} onChange={e => setEmb(p => ({ ...p, [k]: e.target.value }))}
                         className="mt-1 w-full px-2 py-1.5 rounded border border-dark-600 bg-dark-900 text-dark-100 text-sm" />
                </label>
              ))}
            </div>
            <button className={prim} disabled={!Object.values(emb).every(v => num(v) > 0)}
                    onClick={() => { eu(`${emb.peso_kg} kg · ${emb.altura_cm} × ${emb.largura_cm} × ${emb.comprimento_cm} cm`); iniciarFicha(); }}>
              <Check className="w-4 h-4" /> Confirmar
            </button>
          </div>
        );
      case 'fichaResumo':
        return (<>
          <button className={prim} onClick={() => { eu(filaFicha.length ? 'Vamos' : 'Continuar'); perguntarCampo(filaFicha); }}>
            <Check className="w-4 h-4" /> {filaFicha.length ? 'Vamos' : 'Continuar'}
          </button>
          {filaFicha.length > 0 && (
            <button className={sec} onClick={() => { eu('Completo depois'); setFilaFicha([]); perguntarMidias(); }}>
              <SkipForward className="w-4 h-4" /> Completo depois
            </button>
          )}
        </>);
      case 'fichaCampo':
        return campoAtual ? (
          <div className="w-full space-y-2">
            {campoAtual.valores.length > 0 && campoAtual.valores.length <= 16 && (
              <div className="flex flex-wrap gap-1.5">
                {campoAtual.valores.map(v => <button key={v.id} className={sec} onClick={() => responderCampo(v.name)}>{v.name}</button>)}
              </div>
            )}
            {campoAtual.valores.length > 16 && (
              <select className="w-full px-3 py-2 rounded-lg border border-dark-600 bg-dark-900 text-dark-100 text-sm" value=""
                      onChange={e => e.target.value && responderCampo(e.target.value)}>
                <option value="">Escolha…</option>
                {campoAtual.valores.map(v => <option key={v.id} value={v.name}>{v.name}</option>)}
              </select>
            )}
            {!campoAtual.obrigatorio && <button className={sec} onClick={() => responderCampo(null)}><SkipForward className="w-4 h-4" /> Pular</button>}
          </div>
        ) : null;
      case 'midias':
        return (<>
          <label className={clsx(prim, 'cursor-pointer')}>
            <Upload className="w-4 h-4" /> Enviar fotos/vídeos
            <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={e => e.target.files && onMidias(e.target.files)} />
          </label>
          <button className={sec} onClick={() => { eu(imagens.length > 1 ? 'Já enviei tudo' : 'Não tenho mais'); irParaRevisao(); }}>
            {imagens.length > 1 ? 'Já enviei tudo' : 'Não tenho mais'}
          </button>
        </>);
      case 'revisao':
        return (<>
          <button className={prim} onClick={criar}><Check className="w-4 h-4" /> Criar produto</button>
          <button className={sec} onClick={() => onAbrirFormulario(montarProduto(), mps)}><FileEdit className="w-4 h-4" /> Revisar no formulário completo</button>
        </>);
      case 'fim':
        return <button className={prim} onClick={onClose}>Fechar</button>;
    }
    return null;
  })();

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-dark-950 rounded-xl border border-dark-700 w-full max-w-2xl h-[88vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-dark-700">
          <div className="flex items-center gap-2">
            <Bot className="w-6 h-6 text-primary-500" />
            <div>
              <h2 className="text-lg font-bold text-dark-100">Cadastro via chat</h2>
              <p className="text-xs text-dark-500">Eu pergunto, você aprova — no fim o produto é criado.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-dark-400 hover:bg-dark-800" title="Fechar"><X className="w-5 h-5" /></button>
        </div>

        {/* Conversa */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-dark-900">
          {msgs.map(m => (
            <div key={m.id} className={clsx('flex gap-2', m.from === 'user' && 'flex-row-reverse')}>
              <div className={clsx('w-7 h-7 rounded-full flex items-center justify-center shrink-0',
                m.from === 'bot' ? 'bg-primary-100 text-primary-600' : 'bg-dark-700 text-dark-200')}>
                {m.from === 'bot' ? <Bot className="w-4 h-4" /> : <User className="w-4 h-4" />}
              </div>
              <div className={clsx('max-w-[80%] rounded-xl px-3 py-2 text-sm whitespace-pre-line',
                m.from === 'bot' ? 'bg-dark-950 border border-dark-700 text-dark-100' : 'bg-primary-600 text-white')}>
                {m.img && <img src={m.img} alt="" className="mb-2 rounded-lg max-h-48 object-contain" />}
                {fmt(m.text)}
              </div>
            </div>
          ))}

          {passo === 'revisao' && (
            <div className="rounded-xl border border-dark-700 bg-dark-950 p-4 text-sm space-y-2">
              <div className="flex gap-3">
                {imagens[0] && <img src={imagens[0]} alt="" className="w-20 h-20 rounded-lg object-cover" />}
                <div>
                  <p className="font-semibold text-dark-100">{titulo}</p>
                  <p className="text-dark-400">{marca || 'Sem marca'} · {categoria || 'sem categoria'}</p>
                  <p className="text-dark-200">SKU <strong>{sku}</strong> · {brl(num(preco))} · estoque {estoque}</p>
                </div>
              </div>
              <p className="text-dark-400">EAN: {ean || '—'} · Embalagem: {emb.peso_kg} kg, {emb.altura_cm}×{emb.largura_cm}×{emb.comprimento_cm} cm · {imagens.length} mídia(s)</p>
              <p className="text-dark-400">Marketplaces: {mps.length ? mps.map(m => `${nomeMp(m)}${catIds[m] ? '' : ' (sem categoria)'}`).join(', ') : 'nenhum'}</p>
              {attrs.length > 0 && <p className="text-dark-400">Atributos: {attrs.map(a => `${a.name}: ${a.value}`).join(' · ')}</p>}
              {mps.some(m => !catIds[m]) && (
                <p className="text-amber-900 bg-amber-50 border border-amber-300 rounded p-2 text-xs">
                  Algum marketplace está sem categoria — use “Revisar no formulário completo” para escolher antes de publicar.
                </p>
              )}
            </div>
          )}

          {busy && (
            <div className="flex items-center gap-2 text-sm text-dark-400"><Loader className="w-4 h-4 animate-spin" /> {busy}</div>
          )}
          <div ref={fimRef} />
        </div>

        {/* Ações */}
        <div className="p-3 border-t border-dark-700 space-y-2 bg-dark-950">
          {acoes && <div className="flex flex-wrap gap-2">{acoes}</div>}
          {esperaTexto && !busy && (
            <div className="flex gap-2">
              {passo === 'editaDescricao' ? (
                <textarea value={texto} onChange={e => setTexto(e.target.value)} rows={5}
                          className="flex-1 px-3 py-2 rounded-lg border border-dark-600 bg-dark-900 text-dark-100 text-sm" />
              ) : (
                <input value={texto} onChange={e => setTexto(e.target.value)} onKeyDown={e => e.key === 'Enter' && enviarTexto()}
                       autoFocus placeholder={passo === 'fichaCampo' ? 'Ou digite o valor…' : 'Digite aqui…'}
                       className="flex-1 px-3 py-2 rounded-lg border border-dark-600 bg-dark-900 text-dark-100 text-sm" />
              )}
              <button className={prim} onClick={enviarTexto}>Enviar</button>
            </div>
          )}
          {erroInput && <p className="text-xs text-red-700">{erroInput}</p>}
        </div>
      </div>
    </div>
  );
}
